// utils/qrTokenPrefetch.js
// Tiny in-memory cache of minted check-in QR payloads, keyed by eventId, so
// the QR screen can paint instantly. Pure (no react-native / firebase): the
// caller injects the mint function, which resolves to the service's
// `{ data: { payload, generated_at }, error }` shape.
//
// Freshness follows the TOKEN's age (server `generated_at`), not when we
// fetched it: mintEventQRToken hands back the SAME token until it is
// QR_TOKEN_TTL_MS old and scans reject it at exactly that age, so the screen
// re-mints just after expiry (msUntilRotation) — a fixed 8-minute timer could
// keep an expired code on screen for minutes (Codex R1, 2026-10-14).

// Mirrors QR_TOKEN_TTL_MS in firebase-functions/eventCheckIn.js.
export const QR_TOKEN_TTL_MS = 10 * 60 * 1000;
// A cached token with less life left than this is treated as stale.
const MIN_REMAINING_MS = 60 * 1000;
// Re-mint this long after expiry so the server is sure to issue a new token.
const ROTATE_GRACE_MS = 1500;
const MIN_ROTATE_DELAY_MS = 5000;

const cache = new Map(); // eventId -> { payload, generatedAt }
const inFlight = new Map(); // eventId -> Promise<void>

// ISO string / millis / Date -> millis, or null. Local on purpose: returns null for
// invalid input, unlike utils/chatMessage.js toMillis (which returns 0).
function toMillis(value) {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : typeof value === "number" ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

// Delay until the token generated at `generatedAt` should be replaced.
export function msUntilRotation(generatedAt, now = Date.now()) {
  const g = toMillis(generatedAt);
  if (g == null) return MIN_ROTATE_DELAY_MS;
  return Math.max(MIN_ROTATE_DELAY_MS, g + QR_TOKEN_TTL_MS + ROTATE_GRACE_MS - now);
}

// Cached payload with at least a minute of life left, or null.
export function get(eventId, now = Date.now()) {
  const hit = cache.get(eventId);
  if (!hit || now >= hit.generatedAt + QR_TOKEN_TTL_MS - MIN_REMAINING_MS) return null;
  return hit;
}

// Store a minted payload (the screen's own refresh loop or a prefetch).
export function set(eventId, payload, generatedAt) {
  const g = toMillis(generatedAt);
  if (eventId && payload && g != null) cache.set(eventId, { payload, generatedAt: g });
}

// Mint once per eventId while a usable token is cached or a mint is in
// flight. Never throws; errors are dropped (the screen mints again on open
// and surfaces them there).
export function prefetch(eventId, mintFn, now = Date.now()) {
  if (!eventId || typeof mintFn !== "function") return Promise.resolve();
  if (get(eventId, now)) return Promise.resolve();
  const pending = inFlight.get(eventId);
  if (pending) return pending;
  const run = (async () => {
    try {
      const { data, error } = (await mintFn(eventId)) || {};
      if (!error && data?.payload) set(eventId, data.payload, data.generated_at);
    } catch {
      // ignore: prefetch is best-effort
    } finally {
      inFlight.delete(eventId);
    }
  })();
  inFlight.set(eventId, run);
  return run;
}

export function clear(eventId) {
  if (eventId === undefined) cache.clear();
  else cache.delete(eventId);
}
